import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import AppUtils from "../../../Utils/utils"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { USERS_SLICE_NAME } from "./users.constants"
import {
  deleteUser,
  deleteUserComment,
  deleteUserCommentAttachment,
  getUserBusinessAccountsList,
  getUserComment,
  getUserInfoComment,
  getUsers,
  getUsersExport,
  getUsersFilterOptions,
  gift,
  saveComment,
  switchToLoyalBilling,
  withdraw
} from "./users.thunks"

const columnsAdmin = [
  { field_id: "user_full_name", label: "User full name", active: false },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "created_timestamp", label: "Creation date", active: true },
  { field_id: "locked", label: "Locked", active: true },
  { field_id: "last_lock_at", label: "Last lock at", active: true },
  { field_id: "last_active", label: "Last activity", active: true },
  { field_id: "categories", label: "Type", active: true },
  { field_id: "balance_current", label: "Current Balance", active: true },
  { field_id: "alerts_in_debt_count", label: "Alerts in Debt", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "ba_status", label: "BA status", active: true },
  { field_id: "role", label: "Role", active: true },
  { field_id: "role_status", label: "Role status", active: true }
]

const initialState = {
  fetching: false,
  saveFetching: false,
  switchToLoyalBillingFetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    status: "all",

    user_full_name: [],
    user_email: [],
    created_timestamp: null,
    last_active: null,

    balance_current: { min: null, max: null, type: null },
    alerts_in_debt_count: { min: null, max: null, type: null },

    business_account: [],
    ba_status: "all",
    role: "all",
    role_status: "all"
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    ba_status: [],
    role: [],
    role_status: []
  },

  visibleFiltersAdmin: { status: true, user_email: true, user_full_name: true },

  exportFetching: false,
  exportResult: null,

  saveResult: null,

  commentsFetching: false,
  userInfoComment: null,
  userCommentData: null,
  userBusinessAccountsList: []
}

// Shared tail for every request that fails through the old `usersFailure` action -
// clears every "is this in flight" flag the legacy reducer's single failure handler
// touched, regardless of which specific request actually failed.
const stopAllFetching = (state) => {
  state.fetching = false
  state.saveFetching = false
  state.switchToLoyalBillingFetching = false
}

const stopCommentsFetching = (state) => {
  state.commentsFetching = false
}

const slice = createSlice({
  name: USERS_SLICE_NAME,
  initialState,
  reducers: {
    usersSetProp: (state, action) => {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearUsersFilters: (state) => {
      state.filters = initialState.filters
    },
    clearUserCommentsData: (state) => {
      state.userInfoComment = null
      state.userCommentData = null
      state.userBusinessAccountsList = []
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- filter options ---------------- */
      .addCase(getUsersFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getUsersFilterOptions.fulfilled, (state, action) => {
        const filterOptions = action.payload
        state.fetchingFilterOptions = false
        state.filterOptions = {
          ...filterOptions,
          ba_status: filterOptions?.business_account_status || filterOptions?.ba_status
        }
      })
      .addCase(getUsersFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- users list ---------------- */
      .addCase(getUsers.pending, (state) => {
        state.fetching = true
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        if (result.results) {
          state.result = {
            ...result,
            results: result.results?.flatMap(({ business_accounts, ...item }) =>
              business_accounts?.length
                ? business_accounts?.map((account) => ({
                    ...item,
                    row_id: `${item?.id}_${account?.id}_${AppUtils.randomKey()}`,
                    business_account: account
                  }))
                : {
                    ...item,
                    row_id: `${item?.id}_${AppUtils.randomKey()}`,
                    business_account: null
                  }
            )
          }
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getUsers.rejected, stopAllFetching)

      /* ---------------- delete user ---------------- */
      .addCase(deleteUser.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        const deletedBA = action.payload
        const newResults = state.result.results.filter(
          (el) => el.id !== (deletedBA?.id || deletedBA)
        )
        state.fetching = false
        // Preserves the legacy reducer's own math verbatim: `result` is `{ count, ... }`,
        // not an array, so `result.length` is undefined here too - not "fixing" this as
        // part of the RTK port.
        state.result = {
          count: state.result?.length - 1,
          results: newResults
        }
        state.saveResult = "user_delete"
      })
      .addCase(deleteUser.rejected, stopAllFetching)

      /* ---------------- export ---------------- */
      .addCase(getUsersExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getUsersExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getUsersExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- gift / withdraw ---------------- */
      .addCase(gift.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(gift.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = { type: "giftRequest", data: action.payload }
      })
      .addCase(gift.rejected, stopAllFetching)
      .addCase(withdraw.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(withdraw.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = { type: "withdrawRequest", data: action.payload }
      })
      .addCase(withdraw.rejected, stopAllFetching)

      /* ---------------- loyal billing ---------------- */
      .addCase(switchToLoyalBilling.pending, (state) => {
        state.switchToLoyalBillingFetching = true
      })
      .addCase(switchToLoyalBilling.fulfilled, (state) => {
        state.switchToLoyalBillingFetching = false
      })
      .addCase(switchToLoyalBilling.rejected, stopAllFetching)

      /* ---------------- user comments ---------------- */
      .addCase(getUserInfoComment.pending, (state) => {
        state.commentsFetching = true
      })
      .addCase(getUserInfoComment.fulfilled, (state, action) => {
        state.commentsFetching = false
        state.userInfoComment = action.payload
      })
      .addCase(getUserInfoComment.rejected, stopAllFetching)

      .addCase(getUserBusinessAccountsList.pending, (state) => {
        state.commentsFetching = true
      })
      .addCase(getUserBusinessAccountsList.fulfilled, (state, action) => {
        state.commentsFetching = false
        state.userBusinessAccountsList = action.payload
      })
      .addCase(getUserBusinessAccountsList.rejected, stopAllFetching)

      .addCase(getUserComment.pending, (state) => {
        state.commentsFetching = true
      })
      .addCase(getUserComment.fulfilled, (state, action) => {
        state.commentsFetching = false
        state.userCommentData = action.payload
      })
      .addCase(getUserComment.rejected, stopAllFetching)

      .addCase(saveComment.pending, (state) => {
        state.commentsFetching = true
      })
      .addCase(saveComment.fulfilled, (state, action) => {
        state.commentsFetching = false
        state.userCommentData = action.payload
      })
      .addCase(saveComment.rejected, stopAllFetching)

      .addCase(deleteUserComment.pending, (state) => {
        state.commentsFetching = true
      })
      .addCase(deleteUserComment.fulfilled, (state) => {
        state.commentsFetching = false
        state.userCommentData = { ...state.userCommentData, comment_text: null }
      })
      .addCase(deleteUserComment.rejected, stopAllFetching)

      .addCase(deleteUserCommentAttachment.pending, (state) => {
        state.commentsFetching = true
      })
      .addCase(deleteUserCommentAttachment.fulfilled, stopCommentsFetching)
      .addCase(deleteUserCommentAttachment.rejected, stopAllFetching)

      /* ---------------- shared logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["admin-customer-users"]?.data
        if (!preference || preference.version !== USERS_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const usersActions = slice.actions
export const { usersSetProp, clearUsersFilters, clearUserCommentsData } = slice.actions

export const selectAreFiltersDefault = (state) =>
  isEqual(state[USERS_SLICE_NAME].filters, initialState.filters)

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const USERS_PREFERENCES_VERSION = 1

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "users",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
